import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, X, MessageCircle } from 'lucide-react';

/**
 * AdvisorFollowScroll
 *
 * A circular avatar fixed on the right side (desktop) that:
 *  - Observes 6 Home sections via IntersectionObserver.
 *  - Changes its pose (idle / wave / point) depending on which section is in view.
 *  - Shows a contextual bubble per section with a specific CTA.
 *  - Respects prefers-reduced-motion.
 *  - On mobile collapses to a small pin that still shows the bubble on scroll.
 *
 * Clicking the avatar opens the full existing Digital Advisor by clicking its launcher.
 */

const AVATAR_IDLE  = '/img/avatar/advisor-3d-idle.png';
const AVATAR_WAVE  = '/img/avatar/advisor-3d-wave.png';
const AVATAR_POINT = '/img/avatar/advisor-3d-point.png';

const SECTIONS = [
  {
    key: 'hero',
    selector: '[data-testid="hero-title"]',
    pose: AVATAR_WAVE,
    side: 'right',
    bubble: {
      title: 'Ciao, benvenuto!',
      text: 'Sono il tuo Digital Advisor DTS. Posso accompagnarti e aiutarti a trovare la soluzione piu adatta a te.',
      cta: 'Dimmi chi sei',
    },
  },
  {
    key: 'help',
    selector: '[data-testid="home-help-by-profile"]',
    pose: AVATAR_POINT,
    side: 'left',
    glowTarget: '[data-testid="help-card-azienda"]',
    bubble: {
      title: 'Scegli il tuo profilo',
      text: 'Azienda, professionista o privato? Ogni percorso ha le sue soluzioni dedicate.',
      cta: 'Scopri il tuo percorso',
    },
  },
  {
    key: 'ecosystem',
    selector: '[data-testid="home-ecosystem"]',
    pose: AVATAR_POINT,
    side: 'right',
    glowTarget: '[data-testid="ecosystem-node-ai"]',
    bubble: {
      title: 'Un ecosistema connesso',
      text: 'I nostri servizi si potenziano a vicenda: AI, marketing, pagamenti, formazione e totem.',
      cta: 'Esplora i nodi',
    },
  },
  {
    key: 'score',
    selector: '[data-advisor-target="digital-score"]',
    pose: AVATAR_POINT,
    side: 'left',
    bubble: {
      title: 'Non sai da dove partire?',
      text: 'Calcola il tuo DTS Digital Score in 2 minuti e ricevi un piano personalizzato.',
      cta: 'Fai il test',
    },
    fallbackHref: '/digital-score',
  },
  {
    key: 'club',
    selector: '[data-testid="home-club-section"]',
    pose: AVATAR_POINT,
    side: 'right',
    glowTarget: '[data-testid="home-club-cta-discover"]',
    bubble: {
      title: 'Entra nel DTS Club',
      text: 'Una community privata dove idee e persone diventano opportunita.',
      cta: 'Scopri il Club',
    },
    fallbackHref: '/club',
  },
  {
    key: 'reviews',
    selector: '[data-testid="verified-reviews-home"]',
    pose: AVATAR_IDLE,
    side: 'right',
    bubble: {
      title: 'Clienti soddisfatti',
      text: 'Oltre 1.200 attivita hanno gia scelto DTS. Ascolta cosa dicono.',
      cta: 'Leggi le recensioni',
    },
  },
];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function AdvisorFollowScroll() {
  const [activeKey, setActiveKey] = useState(null);
  const [bubbleOpen, setBubbleOpen] = useState(true);
  const [hidden, setHidden] = useState(false);
  const observersRef = useRef([]);
  const seenRef = useRef(new Set());

  useEffect(() => {
    // Observe sections to detect active one
    const timer = setTimeout(() => {
      const io = new IntersectionObserver(
        (entries) => {
          // Prefer the entry closest to viewport center
          const visible = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
          if (!visible.length) return;
          const topEl = visible[0].target;
          const key = topEl.getAttribute('data-advisor-key');
          if (key) setActiveKey(key);
        },
        { threshold: [0.25, 0.5, 0.75], rootMargin: '-20% 0px -30% 0px' },
      );
      SECTIONS.forEach((s) => {
        const el = document.querySelector(s.selector);
        if (el) {
          el.setAttribute('data-advisor-key', s.key);
          io.observe(el);
          observersRef.current.push({ el, io });
        }
      });
      // Initial: find first visible
      if (!activeKey) setActiveKey('hero');
      return () => { try { io.disconnect(); } catch {} };
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, []);

  // Auto-close bubble after a few seconds for sections already seen (less noisy)
  useEffect(() => {
    if (!activeKey) return;
    if (seenRef.current.has(activeKey)) {
      setBubbleOpen(true);
      const t = setTimeout(() => setBubbleOpen(false), 5000);
      return () => clearTimeout(t);
    }
    seenRef.current.add(activeKey);
    setBubbleOpen(true);
    const t = setTimeout(() => setBubbleOpen(false), 9000);
    return () => clearTimeout(t);
  }, [activeKey]);

  const current = SECTIONS.find((s) => s.key === activeKey) || SECTIONS[0];

  // Glow effect on indicated target (if any)
  useEffect(() => {
    if (!current || !current.glowTarget) return;
    const el = document.querySelector(current.glowTarget);
    if (!el) return;
    el.classList.add('advisor-glow');
    const t = setTimeout(() => el.classList.remove('advisor-glow'), 4500);
    return () => {
      el.classList.remove('advisor-glow');
      clearTimeout(t);
    };
  }, [current]);

  const openAdvisor = () => {
    const launcher = document.querySelector('[data-testid="advisor-launcher"]');
    if (launcher) launcher.click();
  };

  const handleCta = () => {
    if (current.fallbackHref) window.location.href = current.fallbackHref;
    else openAdvisor();
  };

  if (hidden) {
    return (
      <button
        type="button"
        onClick={() => setHidden(false)}
        data-testid="advisor-follow-reactivate"
        aria-label="Riattiva assistente"
        className="fixed right-3 bottom-28 md:bottom-32 z-[52] w-10 h-10 rounded-full bg-background border-2 border-lime/60 flex items-center justify-center shadow-xl hover:scale-110 transition-all"
      >
        <Sparkles size={14} className="text-lime" />
      </button>
    );
  }

  const reduce = prefersReducedMotion();
  const sideClass = current?.side === 'left'
    ? 'left-5 items-start'
    : 'right-5 items-end';

  return (
    <div
      className={`hidden md:flex fixed ${sideClass} top-1/2 -translate-y-1/2 z-[52] flex-col gap-2 pointer-events-none transition-[left,right] duration-700 ease-in-out`}
      aria-live="polite"
      data-testid="advisor-follow"
    >
      {/* Bubble */}
      {bubbleOpen && current.bubble && (
        <div
          className="pointer-events-auto max-w-[280px] rounded-2xl border border-lime/40 bg-background/95 backdrop-blur-md shadow-2xl p-4 relative"
          style={reduce ? {} : { animation: 'advisorBubbleIn 0.35s cubic-bezier(0.2, 0.9, 0.3, 1.3)' }}
          data-testid={`advisor-follow-bubble-${current.key}`}
        >
          <button
            onClick={() => setBubbleOpen(false)}
            data-testid="advisor-follow-close-bubble"
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-background border border-white/15 flex items-center justify-center text-white/60 hover:text-white"
            aria-label="Chiudi messaggio"
          >
            <X size={11} />
          </button>
          <p className="text-lime text-[10px] font-mono-t uppercase tracking-widest mb-1">_digital advisor</p>
          <p className="font-display font-bold text-white text-sm leading-tight mb-1">{current.bubble.title}</p>
          <p className="text-white/70 text-xs leading-relaxed mb-3">{current.bubble.text}</p>
          <button
            onClick={handleCta}
            data-testid={`advisor-follow-cta-${current.key}`}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-lime text-background font-semibold text-xs hover:scale-105 transition-transform"
          >
            {current.bubble.cta}
          </button>
        </div>
      )}

      {/* Avatar circle */}
      <div className="pointer-events-auto relative">
        <button
          type="button"
          onClick={openAdvisor}
          data-testid="advisor-follow-avatar"
          aria-label="Apri Digital Advisor"
          className="relative w-[96px] h-[96px] rounded-full overflow-hidden border-2 border-lime shadow-2xl hover:scale-110 transition-transform group"
          style={!reduce ? { animation: 'advisorFloat 4s ease-in-out infinite' } : {}}
        >
          <img
            src={current.pose}
            alt="Digital Advisor"
            className="w-full h-full object-cover transition-opacity duration-500"
            onError={(e) => { e.currentTarget.src = AVATAR_IDLE; }}
          />
          {/* Pulse ring */}
          {!reduce && (
            <span
              className="absolute inset-0 rounded-full border-2 border-lime/60 pointer-events-none"
              style={{ animation: 'advisorPulseRing 2.4s ease-out infinite' }}
            />
          )}
          {/* Mini chat badge */}
          <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-lime border-2 border-background flex items-center justify-center">
            <MessageCircle size={10} className="text-background" />
          </span>
        </button>

        {/* Hide button under avatar */}
        <button
          onClick={() => setHidden(true)}
          data-testid="advisor-follow-hide"
          aria-label="Nascondi assistente"
          className="absolute -bottom-2 left-1/2 -translate-x-1/2 translate-y-full mt-1 text-[9px] font-mono-t text-white/40 hover:text-white"
        >
          nascondi
        </button>
      </div>

      <style>{`
        @keyframes advisorBubbleIn {
          0% { opacity: 0; transform: translateX(14px) scale(0.9); }
          100% { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes advisorFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes advisorPulseRing {
          0%   { transform: scale(1);   opacity: 0.6; }
          80%  { transform: scale(1.5); opacity: 0;   }
          100% { transform: scale(1.5); opacity: 0;   }
        }
        .advisor-glow {
          position: relative;
          z-index: 2;
          animation: advisorTargetGlow 2s ease-in-out 2;
        }
        @keyframes advisorTargetGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(198,242,78,0); }
          50% { box-shadow: 0 0 0 6px rgba(198,242,78,0.5), 0 0 40px 10px rgba(198,242,78,0.35); }
        }
      `}</style>
    </div>
  );
}
